import { isError } from "@faire/typescript";

import { Attributes, LoggedError } from "../../logging/types";
import { UnknownError } from "../../UnknownError";

type NormalizedArgs = {
  message: string;
  error: Error | undefined;
  attributes: Attributes | undefined;
};

/**
 * Extracts or creates an error object from the normalized logging arguments.
 */
export const getError = ({
  message,
  error,
  attributes,
}: NormalizedArgs): LoggedError => {
  if (error) {
    return { argument: true, instance: error };
  } else if (attributes) {
    const attributeValues = Object.values(attributes);
    for (const value of attributeValues) {
      if (isError(value)) {
        return { argument: true, instance: value };
      }
    }
  }
  return { argument: false, instance: new UnknownError(message) };
};

/**
 * Convert an error object into a JSON format.
 *
 * Keep every enumerable field. Global redaction of `config` / `request` /
 * `response` would drop payloads that S2S `logResponse` and similar callers
 * mean to log. Apps that must omit those fields should do it in an interceptor.
 */
export const serializeError = (error: Error): Error & { kind: string } => {
  return {
    ...error,
    message: error.message,
    // DataDog expects "kind" instead of "name"
    kind: error.name,
    name: error.name,
    stack: error.stack,
  };
};

/**
 * Normalize the aguments passed to overloaded logging methods into a consistent structure.
 */
export const normalizeArgs = (
  args: [unknown, unknown?, Attributes?]
): NormalizedArgs => {
  const [messageOrError, attributesOrError, attributes] = args;
  if (attributes) {
    // logger.method(message, error, attributes)
    const error = new UnknownError(attributesOrError);
    return {
      message: messageOrError as string,
      error,
      attributes,
    };
  } else if (typeof messageOrError === "string") {
    if (isError(attributesOrError)) {
      // logger.method(message, error)
      return {
        message: messageOrError,
        error: attributesOrError,
        attributes: undefined,
      };
    } else {
      // logger.method(message, attributes)
      return {
        message: messageOrError,
        error: undefined,
        attributes: attributesOrError as Attributes | undefined,
      };
    }
  } else {
    // logger.method(error, attributes)
    const error = new UnknownError(messageOrError);
    return {
      message: error.message,
      error,
      attributes: attributesOrError as Attributes | undefined,
    };
  }
};

export const isNodeEnvironment = () => {
  return (
    typeof process !== "undefined" &&
    typeof process.stdout !== "undefined" &&
    typeof process.stderr !== "undefined"
  );
};

export const isJestEnvironment = () => {
  return (
    typeof process !== "undefined" &&
    typeof process.env !== "undefined" &&
    process.env.NODE_ENV === "test"
  );
};
